import { Axis, Quaternion, Tools, Vector2, Vector3 } from '@babylonjs/core';

import { CameraInputParam, TouchData } from './data';
import {
  DoubleTouchCrossRotation,
  DoubleTouchPinchZoom,
  DoubleTouchXAxisRotationAll,
  DoubleTouchYAxisRotation,
  SingleTouchPanning,
  SingleTouchRotation,
  SingleTouchZoom,
} from './inputs';
import { SingleTouchWindowView } from './inputs/SingleTouchWindowView';
import { ZEDCamera } from './ZEDCamera';
import { InputCanvasProps } from './ZEDCameraInputManager';

const ZOOM_SENSIBILITY = 0.00002;
const ZOOM_PANNING_RATIO = 0;
const MOBILE_ROTATION_SENSIBILITY_X = 0.002;
const MOBILE_ROTATION_SENSIBILITY_Y = 0.002;
const PIVOT_ROTATION_SENSIBILITY = 1.1;
export const RADIUS_MAX = 1000;
export const RADIUS_MIN = 10;
export const DEFAULT_RADIUS_RATIO = 0.7;

//----------------------------------------
// Inputs
//----------------------------------------
/**
 * 카카오맵 스타일로 미리 정의된 인터페이스를 반환합니다
 * @returns CameraInputParam
 */
export const getKakaoMapStyleInputs = () => {
  SingleTouchPanning.option = { mouseButton: 'all', mouseKeyCode: 'none' };
  SingleTouchRotation.option = {
    mouseButton: 'all',
    mouseKeyCode: 'altKey',
    alpha: true,
    beta: true,
    inertia: 0,
    sensibilityX: MOBILE_ROTATION_SENSIBILITY_X,
    sensibilityY: MOBILE_ROTATION_SENSIBILITY_Y,
  };
  SingleTouchZoom.option = {
    mouseButton: 'all',
    mouseKeyCode: 'ctrlKey',
    sensibility: ZOOM_SENSIBILITY,
    freeMovement: false,
    inertia: 0,
    radiusDampening: 0,
  };
  DoubleTouchXAxisRotationAll.option = {
    alpha: true,
    beta: true,
    inertia: 0,
    sensibilityX: MOBILE_ROTATION_SENSIBILITY_X,
    sensibilityY: MOBILE_ROTATION_SENSIBILITY_Y,
  };
  DoubleTouchPinchZoom.option = {
    sensibility: ZOOM_SENSIBILITY,
    panningRatio: ZOOM_PANNING_RATIO,
    freeMovement: false,
    inertia: 0,
    radiusDampening: 0,
  };
  return {
    desktop: [SingleTouchPanning, SingleTouchRotation, SingleTouchZoom],
    mobile: [
      SingleTouchPanning,
      DoubleTouchYAxisRotation,
      DoubleTouchXAxisRotationAll,
      DoubleTouchPinchZoom,
      DoubleTouchCrossRotation,
    ],
  } as CameraInputParam;
};

export const getKakaoMapStyleInputsForSalesDanji = () => {
  SingleTouchPanning.option = { mouseButton: 'all', mouseKeyCode: 'none' };
  SingleTouchRotation.option = {
    mouseButton: 'all',
    mouseKeyCode: 'altKey',
    alpha: true,
    beta: true,
    inertia: 0,
    sensibilityX: MOBILE_ROTATION_SENSIBILITY_X,
    sensibilityY: MOBILE_ROTATION_SENSIBILITY_Y,
  };
  SingleTouchZoom.option = {
    mouseButton: 'all',
    mouseKeyCode: 'ctrlKey',
    sensibility: ZOOM_SENSIBILITY * 3,
    freeMovement: true,
    inertia: 0,
    radiusDampening: 0,
  };
  DoubleTouchXAxisRotationAll.option = { alpha: true, beta: true, inertia: 0, sensibilityX: 100, sensibilityY: 100 };
  DoubleTouchPinchZoom.option = { sensibility: ZOOM_SENSIBILITY, freeMovement: true, inertia: 0, radiusDampening: 0 };
  return {
    desktop: [SingleTouchPanning, SingleTouchRotation, SingleTouchZoom],
    mobile: [
      SingleTouchPanning,
      DoubleTouchYAxisRotation,
      DoubleTouchXAxisRotationAll,
      DoubleTouchPinchZoom,
      DoubleTouchCrossRotation,
    ],
  } as CameraInputParam;
};

// 입력을 막기 위해 빈 배열을 반환
export const getEmptyInput = () => {
  SingleTouchWindowView.option = { mouseButton: 'all', mouseKeyCode: 'none' };
  return {
    desktop: [],
    mobile: [],
  } as CameraInputParam;
};

export const getRotationInputs = () => {
  SingleTouchRotation.option = {
    mouseButton: 'all',
    mouseKeyCode: 'none',
    alpha: true,
    beta: true,
    inertia: 0,
    sensibilityX: 100,
    sensibilityY: 100,
  };
  DoubleTouchXAxisRotationAll.option = { alpha: true, beta: true, inertia: 0, sensibilityX: 100, sensibilityY: 100 };
  return {
    desktop: [SingleTouchRotation],
    mobile: [DoubleTouchYAxisRotation, DoubleTouchXAxisRotationAll, DoubleTouchCrossRotation],
  } as CameraInputParam;
};

//----------------------------------------
// Move
//----------------------------------------
export const moveByPanning = (camera: ZEDCamera, offset: Vector2, canvasProps: InputCanvasProps) => {
  if (offset.x === 0 && offset.y === 0) return false;

  // temp
  camera.inertia = 0;
  camera.panningInertia = 0;

  const panningMultiplier = camera.position.length() * camera.fov;

  const width = canvasProps.canvasSize.x;
  const height = canvasProps.canvasSize.y;

  offset.x = -(offset.x / width) * panningMultiplier * canvasProps.canvasRatio;
  offset.y = (offset.y / height) * panningMultiplier;

  const newOffset = offset; //this.validatePanning(offset);

  camera.inertialPanningX += newOffset.x;
  camera.inertialPanningY += newOffset.y;

  return true;
};

//----------------------------------------
// Rotate
//----------------------------------------
export const rotateAroundYAxisWithPivot = (
  camera: ZEDCamera,
  offset: number,
  pivot: Vector3,
  angularSensibilityY: number = PIVOT_ROTATION_SENSIBILITY
) => {
  // Calculate the angle of rotation based on the offset and sensibility
  const angleY = offset * angularSensibilityY;

  // Convert angles from degrees to radians
  const angleYRad = Tools.ToRadians(angleY);

  // Calculate the rotation quaternion around the Y axis (up)
  const quaternionY = Quaternion.RotationAxis(Axis.Y, angleYRad);

  let newCameraPosition = camera.position.clone();
  newCameraPosition.rotateByQuaternionAroundPointToRef(quaternionY, pivot, newCameraPosition);
  camera._setPosition(newCameraPosition);

  let newTargetPosition = camera.getTarget().clone();
  newTargetPosition.rotateByQuaternionAroundPointToRef(quaternionY, pivot, newTargetPosition);
  camera.setTarget(newTargetPosition);
};

export const rotateByPanning = (
  camera: ZEDCamera,
  offset: Vector2,
  inertia: number = 0,
  angularSensibilityX: number = MOBILE_ROTATION_SENSIBILITY_X,
  angularSensibilityY: number = MOBILE_ROTATION_SENSIBILITY_Y
) => {
  if (offset.x === 0 && offset.y === 0) return false;

  camera.inertia = inertia;

  if (offset.x !== 0) {
    camera.inertialAlphaOffset -= offset.x * angularSensibilityX;
  }
  if (offset.y !== 0) {
    camera.inertialBetaOffset -= offset.y * angularSensibilityY;
  }

  return true;
};
export const rotateByPanningAlpha = (
  camera: ZEDCamera,
  offset: Vector2,
  inertia: number = 0,
  angularSensibilityX: number = MOBILE_ROTATION_SENSIBILITY_X,
  angularSensibilityY: number = MOBILE_ROTATION_SENSIBILITY_Y
) => {
  if (offset.x === 0) return false;
  offset.y = 0;

  rotateByPanning(camera, offset, inertia, angularSensibilityX, angularSensibilityY);

  return true;
};
export const rotateByPanningBeta = (
  camera: ZEDCamera,
  offset: Vector2,
  inertia: number = 0,
  angularSensibilityX: number = MOBILE_ROTATION_SENSIBILITY_X,
  angularSensibilityY: number = MOBILE_ROTATION_SENSIBILITY_Y
) => {
  if (offset.y === 0) return false;
  offset.x = 0;

  rotateByPanning(camera, offset, inertia, angularSensibilityX, angularSensibilityY);
  return true;
};
export const rotateByRotateAlpha = (camera: ZEDCamera, angle: number, inertia: number = 0) => {
  if (angle === 0) return false;

  camera.inertia = inertia;

  const radians = (angle * Math.PI) / 180;
  camera.inertialAlphaOffset -= radians;
  return true;
};

//----------------------------------------
// Zoom
//----------------------------------------
export const zoomByPanning = (
  camera: ZEDCamera,
  offset: Vector2,
  sensibility: number = 1,
  freeMovement: boolean = false,
  inertia: number = 0
) => {
  if (offset.x === 0 && offset.y === 0) return false;

  const _sensibility = 150 / sensibility;
  const delta = (camera.upperRadiusLimit ?? _sensibility) / _sensibility;
  const radius = -(offset.y - offset.x) * delta;
  const nextRadius = camera.radius + radius;
  const possibleLowerLimit = nextRadius >= (camera.lowerRadiusLimit ?? 0);
  const possibleUpperLimit = camera.upperRadiusLimit ? nextRadius <= camera.upperRadiusLimit : true;

  camera.inertia = inertia;
  if (possibleLowerLimit && possibleUpperLimit) {
    camera.inertialRadiusOffset -= radius;
  } else if (freeMovement && possibleUpperLimit) {
    camera.inertialTargetOffset = camera.inertialTargetOffset.add(
      camera.getDirection(Vector3.LeftHandedForwardReadOnly).scale(-radius)
    );
  }
  return true;
};
export const zoomByWheel = (camera: ZEDCamera, delta: number) => {
  if (delta === 0) return false;

  camera.inertialRadiusOffset -= delta / 200;

  return true;
};
export const zoomByPinch = (
  camera: ZEDCamera,
  pinchDistance: number,
  sensibility: number = 1,
  freeMovement: boolean = false,
  inertia: number = 0
) => {
  if (pinchDistance === 0) return false;

  const radius = (pinchDistance * (camera.upperRadiusLimit ?? 20)) / (250000 / sensibility);
  const nextRadius = camera.radius - radius;

  camera.inertia = inertia;

  const possibleLowerLimit = nextRadius >= (camera.lowerRadiusLimit ?? 0);
  const possibleUpperLimit = camera.upperRadiusLimit ? nextRadius <= camera.upperRadiusLimit : true;
  if (possibleLowerLimit && possibleUpperLimit) {
    camera.inertialRadiusOffset += radius;
  } else if (freeMovement && possibleUpperLimit) {
    camera.inertialTargetOffset = camera.inertialTargetOffset.add(
      camera.getDirection(Vector3.LeftHandedForwardReadOnly).scale(radius)
    );
  }

  return true;
};

// target을 이동하면서 zoom을 하는 경우
export const zoomByPinchWithPanning = (
  camera: ZEDCamera,
  pinchDistance: number,
  panningDirection?: Vector3,
  sensibility: number = 1,
  panningRatio: number = 0, // 줌에서 패닝이 차지하는 비율. 0 ~ 1.0.
) => {
  if (pinchDistance === 0) return false;

  const oldRadius = camera.radius;
  const zoomDistance = pinchDistance * sensibility * oldRadius;

  // panningRatio를 뺀 나머지만큼 zoom
  const diffByZoom = zoomDistance * (1 - panningRatio);
  const nextRadius = camera.radius - diffByZoom;

  // zoom
  const radiusMin = camera.lowerRadiusLimit ?? RADIUS_MIN;
  const radiusMax = camera.upperRadiusLimit ?? RADIUS_MAX;
  const canZoomIn = nextRadius >= radiusMin;
  const canZoomOut = nextRadius <= radiusMax;
  const isZoomIn = zoomDistance > 0;

  // zoom이 없으면 panning도 없다
  if (isZoomIn && !canZoomIn) return false;
  if (!isZoomIn && !canZoomOut) return false;

  camera.inertialRadiusOffset += diffByZoom;

  // panning
  const canPanning = oldRadius > radiusMin && oldRadius < radiusMax;
  if (canPanning) {
    const diffByPanning = panningDirection ? panningDirection.scale(zoomDistance * panningRatio) : Vector3.Zero();
    camera.inertialTargetOffset = camera.inertialTargetOffset.add(diffByPanning);
  } else {
    camera.inertialTargetOffset = Vector3.Zero();
  }

  return true;
};

//----------------------------------------
// FOV
//----------------------------------------
export const fovByPanning = (camera: ZEDCamera, offset: Vector2) => {
  if (offset.x === 0 && offset.y === 0) return;

  const sensibility = 1000;
  const delta = (camera.upperRadiusLimit ?? sensibility) / sensibility;
  const fov = -(offset.y - offset.x) * delta;
  const nextFov = camera.fov - fov;

  if (nextFov < (camera.upperFovLimit ?? 2.5) && nextFov > (camera.lowerFovLimit ?? 0.4)) {
    camera.inertialFov -= fov;
  }
};
export const fovByPinch = (camera: ZEDCamera, pinchDistance: number, sensibility: number = 1) => {
  if (pinchDistance === 0) return false;

  const fov = pinchDistance / (200000 / sensibility);
  const nextFov = camera.fov - fov;

  if (nextFov < (camera.upperFovLimit ?? 2.5) && nextFov > (camera.lowerFovLimit ?? 0.4)) {
    camera.inertialFov -= fov;
  }

  return true;
};

//----------------------------------------
// Position Translate
//----------------------------------------
export const positionByPanningVertical = (camera: ZEDCamera, offset: Vector2) => {
  if (offset.y === 0) return false;

  return positionByPanning(camera, offset.y);
};
export const positionByPanning = (camera: ZEDCamera, delta: number, sensibility: number = 0.1) => {
  const dir = camera.target.subtract(camera.position);
  dir.y = 0;

  const addVec = dir.scale(delta).scale(sensibility);
  camera.setPosition(addVec, true, false, false);
  return true;
};

//----------------------------------------
// Compare Touches
//----------------------------------------
export const getSimilarityBetweenTouch = (touchData: TouchData[]) => {
  if (touchData.length < 2) return 0;
  if (!touchData[0] || !touchData[1]) return 0;

  return touchData[0].getSimilarityBetweenTouchDirection(touchData[1]);
};
export const getSimilarityBetweenTouchDrag = (touchData: TouchData[]) => {
  if (touchData.length < 2) return 0;
  if (!touchData[0] || !touchData[1]) return 0;

  return touchData[0].getSimilarityBetweenTouchDrag(touchData[1]);
};
export const getSimilarityOtherDirection = (touchData: TouchData[]) => {
  if (touchData.length < 2) return 0;
  if (!touchData[0] || !touchData[1]) return 0;

  return touchData[0].getSimilarityOtherDirection(touchData[1].direction);
};
export const getMultiTouchPanPosition = (touchData: TouchData[]) => {
  if (touchData.length < 2) return Vector2.Zero();
  if (!touchData[0] || !touchData[1]) return Vector2.Zero();

  return touchData[1].getMultiTouchPanPosition(touchData[0]);
};
export const getPinchSquaredDistance = (touchData: TouchData[]) => {
  if (touchData.length < 2) return 0;
  if (!touchData[0] || !touchData[1]) return 0;
  return touchData[1].getPinchSquaredDistance(touchData[0]);
};
export const getPinchDelta = (t0: TouchData, t1: TouchData) => {
  const v0 = t0.point.subtract(t1.point).length();
  const v1 = t1.point.add(t1.movement).subtract(t0.point.add(t0.movement)).length();
  return v1 - v0;
};
export const getAngleBetweenTouch = (touchData: TouchData[]) => {
  if (touchData.length < 2) return 0;
  if (!touchData[0] || !touchData[1]) return 0;
  return touchData[1].getAngleBetweenTouch(touchData[0]);
};

//----------------------------------------
// Double Touch Conditions
//----------------------------------------
export const checkXAxisDoubleTouch = (touchData: TouchData[], threshold: number) => {
  if (touchData.length < 2) return false;
  if (!touchData[0] || !touchData[1]) return false;
  const dir1 = touchData[0].direction;
  const dir2 = touchData[1].direction;

  const areXDirectionsSameSign = (dir1.x > 0 && dir2.x > 0) || (dir1.x < 0 && dir2.x < 0);

  const isAboveThreshold = Math.abs(dir1.x) > threshold && Math.abs(dir2.x) > threshold;

  return isAboveThreshold && areXDirectionsSameSign;
};
export const checkYAxisDoubleTouch = (touchData: TouchData[], threshold: number) => {
  if (touchData.length < 2) return false;
  if (!touchData[0] || !touchData[1]) return false;
  const dir1 = touchData[0].direction;
  const dir2 = touchData[1].direction;

  const areYDirectionsSameSign = (dir1.y > 0 && dir2.y > 0) || (dir1.y < 0 && dir2.y < 0);

  const isAboveThreshold = Math.abs(dir1.y) > threshold && Math.abs(dir2.y) > threshold;

  return isAboveThreshold && areYDirectionsSameSign;
};
export const checkPinchDoubleTouch = (touchData: TouchData[]) => {
  return Math.abs(getSimilarityBetweenTouchDrag(touchData)) > 0.6;
};
export const checkCrossDoubleTouch = (touchData: TouchData[]) => {
  if (touchData.length < 2) return false;
  if (!touchData[0] || !touchData[1]) return false;
  // 두 터치포인트 사이의 거리가 100px보다 가까우면 rotation이 아니라고 판단
  return (
    Vector2.DistanceSquared(touchData[0].firstPoint, touchData[1].firstPoint) > 10000 &&
    getSimilarityBetweenTouchDrag(touchData) < 0
  );
};
